import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from "@mui/material";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridSortModel,
} from "@mui/x-data-grid";

import { EntityId } from "@reduxjs/toolkit";

import { dossierExportSelectors } from "./store/selectors.dossierExport";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { caseExportsThunks } from "./store/slice";
import { DossierExport } from "types/DossierExport";
import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";
import { PaginatedRequest } from "types/PaginatedRequest";
import { DossierExportDownloadButton } from "./DossierExportDownloadButton";

export interface DossierExportsOverviewDialogProps {
    dossierId: EntityId;
    closeDialog: () => void;
}

const columns: GridColDef<DossierExport>[] = [
    {
        field: "created",
        headerName: "Created",
        minWidth: 150,
        valueFormatter: (value) =>
            value && DateTime.fromISO(value).toRelative(),
    },
    {
        field: "createdBy",
        headerName: "Created By",
        width: 200,
    },
    {
        field: "recordingCount",
        headerName: "Recording Amount",
        width: 150,
    },
    {
        field: "status",
        headerName: "Status",
        width: 150,
    },
    {
        field: "id",
        headerName: "Download Archive",
        minWidth: 150,
        renderCell: (param: GridRenderCellParams<DossierExport>) => {
            if (param.value) {
                return (
                    <DossierExportDownloadButton dossierExport={param.row} />
                );
            }

            return null;
        },
    },
];

export function DossierExportsOverviewDialog(
    props: DossierExportsOverviewDialogProps,
) {
    const dispatch = useAppDispatch();

    // store
    const dossierExports = useAppSelector(
        dossierExportSelectors.selectAllDossierExports,
    );
    const totalDossierExports = useAppSelector(
        dossierExportSelectors.selectTotalDossierExports,
    );
    const isLoadingDossierExports = useAppSelector(
        dossierExportSelectors.selectIsLoadingDossierExports,
    );
    const errorLoadingDossierExports = useAppSelector(
        dossierExportSelectors.selectErrorLoadingDossierExports,
    );

    // // state and handlers
    const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
        { field: "created", sort: "desc" },
    ]); // default sort

    const [paginatedRequest, setPaginatedRequest] = useState<PaginatedRequest>({
        page: 0,
        pageSize: 8,
    });

    useEffect(() => {
        const orderByString =
            gridSortModel?.map((x) => `${x.field} ${x.sort}`).join(",") ?? "";

        dispatch(
            caseExportsThunks.loadDossierExportsForDossierAsync({
                dossierId: props.dossierId,
                ...paginatedRequest,
                page: paginatedRequest.page + 1,
                orderBy: orderByString,
            }),
        );
    }, [dispatch, paginatedRequest, gridSortModel]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={"lg"}
            onClose={(event: object, reason: string) => {
                if (reason !== "backdropClick") {
                    props.closeDialog();
                }
            }}
        >
            <DialogTitle>Case Exports</DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    {isLoadingDossierExports && (
                        <CircularProgress title="Loading exports..." />
                    )}

                    {!isLoadingDossierExports &&
                        totalDossierExports !== undefined && (
                            <DataGrid
                                density={"standard"}
                                rows={dossierExports}
                                rowCount={totalDossierExports}
                                columns={columns}
                                paginationMode={"server"}
                                paginationModel={paginatedRequest}
                                onPaginationModelChange={setPaginatedRequest}
                                sortingMode={"server"}
                                onSortModelChange={setGridSortModel}
                                sortModel={gridSortModel}
                                rowSelection={false}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: paginatedRequest.pageSize,
                                        },
                                    },
                                }}
                            />
                        )}

                    {errorLoadingDossierExports && (
                        <GlobalErrorMessage
                            title={"Unable to fetch dossier exports"}
                            message={"Please contact the service desk."}
                            debugData={{ errorLoadingDossierExports }}
                            alertSeverity={"error"}
                        />
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => {
                        // cancel dialog
                        props.closeDialog();
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
